import type { StudioApp, StudioDocument } from './studio-api';
import { uiFormat, uiIn, uiText } from '../../i18n';

/** Читаем только публичные поля. Служебный JSON не выводится в интерфейс. */
export const record = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
export const rows = (value: unknown): Record<string, unknown>[] => (Array.isArray(value) ? value.map(record) : []);
export const text = (value: unknown): string => (typeof value === 'string' ? value : '');
export const title = (value: Record<string, unknown>): string => text(value.name) || text(value.title);
/** Разряды числа не расходятся по строкам («2 400» — engine.ts:424, nb): только для показа, ключ-название не меняется.
    Эталон применяет nb к тексту кадра слайдера (slideHTML: заголовок и подпись), а не к названиям. */
export const nb = (value: string): string => value.replace(/(\d) (\d{3})(?!\d)/g, '$1\u00a0$2');
/** Короткий предлог или союз не остаётся в конце строки («Кружок из␣видео» — engine.ts:424, nbw): только для показа.
    Эталон применяет nbw к названию в разметке плитки шаблона (appTileHTML), плитки инструмента (toolTileHTML),
    строки инструмента (toolRowHTML) и строки шаблона (appRowHTML) — и НЕ применяет к названию подборки (collName = esc)
    и к строке «Сейчас в тренде» (trendHTML = esc). */
export const nbw = (value: string): string => value.replace(/(^|\s)([А-ЯЁа-яё]{1,2}) /g, '$1$2\u00a0');
export function documentKind(value: Record<string, unknown>): 'app' | 'collection' | 'tool' | 'reel_template' {
  const kind = value.document_kind ?? value.kind;
  return kind === 'app_collection' || kind === 'collection'
    ? 'collection'
    : kind === 'tool'
      ? 'tool'
      : kind === 'reel_template'
        ? 'reel_template'
        : 'app';
}
/** Форматы шаблона, для которых есть лист предпросмотра (AppTile.format, docs/03-api.md §6.2) */
const SHEET_FORMATS = new Set(['reels', 'post', 'photo', 'video', 'circle', 'avatar', 'slides']);
/** Приложение каталога — вход в экран инструмента, а не шаблон: во входе выбран инструмент экрана (tool, preset)
    или формат вне форматов шаблона (text, audio, image). Такое открывается сразу, без листа «Что нужно»;
    формата нет — по-прежнему лист. */
/** Экраны инструментов, у которых нет шаблонов: «Карточка для маркетплейса», «Слайды с преимуществами», «Фотосессия товара» и др.
    Приложение с таким входом — сам инструмент. Формат здесь не решает: список подборки отдаёт им формат группы фильтра
    (image → photo), и «Карточка» из «Для бизнеса» открывала лист фотосессии с селфи (стенд 29.10) */
const TOOL_SCREENS = new Set(['mcard', 'infog', 'pshoot', 'collage', 'text', 'voice', 'cover', 'script']);
export function opensScreen(value: Record<string, unknown>): boolean {
  const entry = record(value.entry);
  const screen = text(entry.screen);
  if (!screen) return false;
  if (TOOL_SCREENS.has(screen)) return true;
  const format = text(value.format);
  return !!(text(entry.tool) || text(entry.preset)) || (!!format && !SHEET_FORMATS.has(format));
}
/** Название для показа. `title` остаётся без изменений: он же ключ (поиск, сортировка, aria-label) — в эталоне
    ключ строки и aria берут сырое имя (data-k="t:${esc(x.n)}", act(x.n)), а неразрывный пробел живёт только в разметке.
    У подборки название без nbw: эталон выводит его через collName (esc), см. engine.ts:554, 1245. */
export const displayTitle = (value: Record<string, unknown>): string =>
  documentKind(value) === 'collection' ? title(value) : nbw(title(value));
/** Конверт ленты items[] = { kind, app | collection | tool } (docs/03-api.md §6.2) → плоский документ.
    Ничего не отбрасывает: решает, годится ли запись, тот, кто её показывает. */
export function unwrapTile(value: Record<string, unknown>): StudioDocument {
  const nested = record(value.app ?? value.collection ?? value.tool);
  return (
    Object.keys(nested).length
      ? {
          ...nested,
          document_kind: value.kind === 'collection' ? 'app_collection' : value.kind,
          // признак витрины стоит у элемента ленты, а не внутри подборки (§6.5)
          ...(value.hero === true ? { hero: true } : {}),
        }
      : value
  ) as StudioDocument;
}
/** У подборки идентификатор — код строкой («twilight», «scenes», docs/03-api.md §6.4), у шаблона — число */
function usableId(source: StudioDocument): boolean {
  if (documentKind(source) === 'collection') return typeof source.id === 'string' ? /^[a-z0-9_-]{1,64}$/i.test(source.id) : Number.isSafeInteger(source.id) && Number(source.id) > 0;
  return Number.isSafeInteger(source.id) && Number(source.id) > 0;
}
export function tile(value: Record<string, unknown>): StudioDocument | null {
  const source = unwrapTile(value);
  return usableId(source) && title(source) ? source : null;
}
export function isApp(value: StudioDocument): value is StudioApp {
  return documentKind(value) === 'app' && !!text(record(value.entry).screen);
}
/** Стенд на моках (`npm run dev:api`, VITE_MOCK_API) отдаёт картинки по http с localhost.
    Флаг ставит только этот стенд: ни сборка, ни прогон тестов его не включают,
    поэтому везде, кроме стенда, правило прежнее — абсолютный https без логина и пароля. */
const stand = () => (import.meta.env.DEV || import.meta.env.MODE === 'test') && import.meta.env.VITE_MOCK_API === 'true';
export function safeMediaUrl(value: unknown, limit = 4096): string | null {
  if (typeof value !== 'string' || value.length > limit) return null;
  /* ключ картинки персонажа (live-avatar/<id>) — не адрес: картинку отдаёт авторизованный BFF (use-studio-artwork.ts).
     Стенд с моками достраивает относительные адреса фикстур, и ключ превращался в несуществующий адрес приложения */
  if (value.startsWith('live-avatar/')) return null;
  try {
    const url = stand() ? new URL(value, location.origin) : new URL(value);
    const local = stand() && url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    return (url.protocol === 'https:' || local) && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}
export function mediaUrl(value: unknown): string | null {
  return safeMediaUrl(value);
}
export function cover(value: Record<string, unknown>): string | null {
  return mediaUrl(
    value.cover_url ?? value.cover ?? record(value.preview).after_url ?? (Array.isArray(value.covers) ? value.covers[0] : null),
  );
}
/** Единица, которая в эталоне идёт отдельным span.u — словами, обычным шрифтом, плашка от этого короче
    (prHTML, engine.ts:1236: `u[0]==='/'?esc(u):`<span class="u">${esc(u.trim())}</span>``). */
const PRICE_UNIT_WORDS: Record<string, string> = { per_minute: 'в минуту', per_100_chars: 'за 100 знаков' };
/** Единица, которая в эталоне стоит в самом тексте — с косой чертой, слитно и моноширинным («4 C/сек»):
    шаблон берётся целиком, чтобы перевод не распался. */
const PRICE_UNIT_INLINE: Record<string, [string, string]> = { per_second: ['{0} C/сек', 'от {0} C/сек'] };

/** Плашка цены по частям: `text` — то, что идёт текстом плашки («от 15 C», «4 C/сек»), `unit` — то, что
    в эталоне вынесено в span.u («за 100 знаков», «в минуту»). Цены нет — обе части пустые.
    «от» строчной: эталон пишет его как отдельную приставку плашки — `${v.from?'от ':''}` (engine.ts:1236). */
/** Единица цены: unit, без него — per; «sec | second | s» админского документа — per_second, как у сервера
    (MessagingStudioItemNormalizer.Duration: цена приложения проходит к клиенту в том виде, в каком её ввёл админ). */
export function priceUnit(value: unknown): string {
  const fields = record(value);
  const unit = text(fields.unit) || text(fields.per);
  return unit === 'sec' || unit === 'second' || unit === 's' ? 'per_second' : unit;
}
export type PriceParts = { text: string; unit: string };
export function priceParts(value: unknown): PriceParts {
  const fields = record(value);
  const price = typeof value === 'number' ? value : (fields.amount ?? fields.circle);
  if (typeof price !== 'number' || !Number.isFinite(price) || price < 0) return { text: '', unit: '' };
  const unit = priceUnit(fields);
  const inline = PRICE_UNIT_INLINE[unit];
  const template = inline ? inline[fields.from ? 1 : 0] : fields.from ? 'от {0} C' : '{0} C';
  const word = PRICE_UNIT_WORDS[unit];
  return { text: uiFormat(template, price), unit: word ? uiText(word) : '' };
}
/** Цена одной строкой — для мест, где разметка не нужна: aria-label, подпись плитки, заголовок листа. */
export function priceText(value: unknown): string {
  const parts = priceParts(value);
  return parts.unit ? `${parts.text} ${parts.unit}` : parts.text;
}

/** «Что нужно» по формату, когда у шаблона нет своего поля: та же таблица, что в прототипе (NEED_F, engine.ts),
    берётся её короткий вариант — строка плитки. Нужно, потому что сервер это поле пока не отдаёт (у шаблона
    в админке своего текста нет, «что нужно» считается по ролям сюжета или по формату — docs/12-admin.md §6.2). */
const NEED_BY_FORMAT: Record<string, string> = {
  post: '1 фото',
  avatar: '1 фото',
  photo: '1 фото',
  video: '1 фото',
  reels: '1 фото',
  circle: 'Текст',
  slides: 'Тема',
  /* форматы стенда, которых в прототипе нет: строки временные и уйдут, как только сервер
     начнёт отдавать need_text (поле шаблона в админке) */
  text: 'Текст',
  audio: 'Запись',
};
/** Строка «что нужно» плитки: своё поле шаблона, иначе — по формату. Пустую строку не выдаём: без неё
    подпись начиналась с висящей точки («· 0 C»). */
export function needText(item: Record<string, unknown>): string {
  const own =
    text(item.need_text) || text(record(item.need).text) || text(item.need) || text(item.count_text);
  if (own) return own;
  const format = text(item.format) || text(record(item.preview).format);
  /* «нужно: …» — своё место в словаре: «Тема» здесь — тема поста, а не оформления */
  return format ? uiIn('нужно', NEED_BY_FORMAT[format] ?? '') : '';
}
